import Styles from './TariffPopOverContainer.module.css'
import Colors from 'Theme/Colors'
import { ChartComponent, IFText } from 'Components'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import InfoIcon from '@mui/icons-material/Info'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

const StyledHyperLink = styled.a`
  font-size: 14;
  vertical-align: middle;
  text-align: center;
  padding-bottom: 2px;
  color: ${(props) => props.textColor};
  &:hover {
    color: ${(props) => props.hoverColor} !important;
  }
  &:before {
    content: '';
    width: 0;
    height: 0.1em;
    position: absolute;
    bottom: 0;
    left: 50%;
    background: ${(props) => props.hoverColor} !important;
    transition: all 0.15s;
  }
  &:hover:before {
    left: 0;
    background: ${(props) => props.hoverColor} !important;
  }
`
const TariffPopOverContainer = ({ tariff, elements, operator }) => {
  const { t } = useTranslation()

  const baseColors = [Colors.primary, Colors.ChargingConnector, Colors.vivid]
  const generateColors = (count) => {
    if (count <= 3) return baseColors

    const colors = [...baseColors]
    const hues = [180, 210, 240, 270, 300, 330]

    for (let i = 3; i < count; i++) {
      colors.push(`hsl(${hues[(i - 3) % hues.length]}, 70%, 50%)`)
    }

    return colors
  }

  const elementsWithTimeRestrictions = elements.filter(
    (el) => el.restrictions?.startTime && el.restrictions?.endTime,
  ).length

  const chartColors = generateColors(Math.max(elementsWithTimeRestrictions, 1))

  const formatTime12Hour = (timestamp) => {
    const date = new Date(timestamp)
    let hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'PM' : 'AM'

    hours = hours % 12
    hours = hours ? hours : 12

    const minutesStr = minutes < 10 ? '0' + minutes : minutes

    return `${hours}:${minutesStr} ${ampm}`
  }

  function buildRangeBarFromElements(elements) {
    const DAY = 24 * 60 * 60 * 1000
    const MINUTE = 60 * 1000

    const hasAnyTimeRestrictions = elements.some(
      (el) => el.restrictions?.startTime && el.restrictions?.endTime,
    )

    if (!hasAnyTimeRestrictions && elements.length > 0) {
      const firstElement = elements[0]
      const pc = firstElement.priceComponents?.[0]
      const price = pc?.price
      const name =
        price != null
          ? `${price.toFixed(3)} ${operator?.country.currency.code}`
          : 'All Day'
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const start = today.getTime()
      const end = start + DAY
      const series = [
        {
          name: name,
          data: [{ x: 'Price', y: [start, end] }],
        },
      ]

      const boundaryAnnotations = [
        {
          x: start,
          borderColor: 'transparent',
          label: {
            text: '12:00 AM',
            borderColor: 'transparent',
            style: { background: 'transparent', color: Colors.DetailPanel },
            orientation: 'horizontal',
            position: 'top',
            offsetY: -8,
            offsetX: 12,
          },
        },
        {
          x: end,
          borderColor: 'transparent',
          label: {
            text: '12:00 AM',
            borderColor: 'transparent',
            style: { background: 'transparent', color: Colors.DetailPanel },
            orientation: 'horizontal',
            position: 'bottom',
            offsetY: 12,
            offsetX: -14,
          },
        },
      ]

      return { series, boundaryAnnotations, min: start, max: end }
    }

    const segments = elements
      .map((el, i) => {
        const r = el.restrictions || {}
        if (!r.startTime || !r.endTime) return null

        let start = Date.parse(r.startTime)
        let end = Date.parse(r.endTime)
        if (Number.isNaN(start) || Number.isNaN(end)) return null

        if (end <= start) end += DAY

        const pc = el.priceComponents?.[0]
        const price = pc?.price
        const name =
          price != null
            ? `${price.toFixed(3)} ${operator?.country.currency.code}`
            : `Segment ${i + 1}`

        return { name, start, end, price }
      })
      .filter(Boolean)
      .sort((a, b) => a.start - b.start)

    const series = segments.map((seg) => ({
      name: seg.name,
      data: [{ x: 'Price', y: [seg.start, seg.end] }],
    }))

    const allBoundaries = segments.flatMap((s) => [s.start, s.end])
    const sortedBoundaries = [...new Set(allBoundaries)].sort((a, b) => a - b)

    const boundaries = []
    for (let i = 0; i < sortedBoundaries.length; i++) {
      const current = sortedBoundaries[i]
      const next = sortedBoundaries[i + 1]

      if (next && next - current <= MINUTE) {
        continue
      }

      boundaries.push(current)
    }

    const boundaryAnnotations = boundaries.map((x, i) => ({
      x,
      borderColor: 'transparent',
      label: {
        text: formatTime12Hour(x),
        borderColor: 'transparent',
        style: { background: 'transparent', color: Colors.DetailPanel },
        orientation: 'horizontal',
        position: i % 2 === 0 ? 'top' : 'bottom',
        offsetY: i % 2 === 0 ? -8 : 12,
        offsetX: i === 0 ? 12 : i === boundaries.length - 1 ? -12 : 0,
      },
    }))

    const min = boundaries[0]
    const max = boundaries[boundaries.length - 1]

    return { series, boundaryAnnotations, min, max }
  }
  const isRestrictionActive = (element) => {
    const r = element.restrictions
    if (!r) return false
    const now = new Date()
    if (r.startTime && r.endTime) {
      const startTime = new Date(r.startTime)
      const endTime = new Date(r.endTime)

      const currentTime = now.getHours() * 60 + now.getMinutes()
      const startMinutes = startTime.getHours() * 60 + startTime.getMinutes()
      let endMinutes = endTime.getHours() * 60 + endTime.getMinutes()

      if (endMinutes <= startMinutes) {
        return currentTime >= startMinutes || currentTime <= endMinutes
      }
      return currentTime >= startMinutes && currentTime <= endMinutes
    }

    return true
  }

  const { series, boundaryAnnotations, min, max } =
    buildRangeBarFromElements(elements)

  const options = {
    chart: {
      type: 'rangeBar',
      toolbar: { show: false },
      zoom: {
        enabled: false,
      },
    },
    plotOptions: {
      bar: { horizontal: true, barHeight: '100%', rangeBarGroupRows: true },
    },
    colors: chartColors,
    xaxis: {
      axisTicks: { show: false },
      type: 'datetime',
      labels: { show: false },
      min: min,
      max: max,
      axisBorder: { show: false },
    },
    yaxis: {
      show: true,
      showAlways: true,
      floating: true,
      axisTicks: { show: false },
      axisBorder: { show: false },
      labels: { show: true },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    annotations: {
      xaxis: boundaryAnnotations,
    },
    tooltip: {
      enabled: false,
    },
  }

  const getColorForElement = (elemIdx) => {
    const elementsWithTime = elements
      .map((el, i) => ({ el, originalIndex: i }))
      .filter(({ el }) => hasTimeRestrictions(el))
      .sort((a, b) => {
        const startA = Date.parse(a.el.restrictions.startTime)
        const startB = Date.parse(b.el.restrictions.startTime)
        return startA - startB
      })

    const colorIndex = elementsWithTime.findIndex(
      ({ originalIndex }) => originalIndex === elemIdx,
    )

    return colorIndex !== -1
      ? chartColors[colorIndex % chartColors.length]
      : chartColors[0]
  }

  const hasTimeRestrictions = (element) => {
    return element.restrictions?.startTime && element.restrictions?.endTime
  }

  const getPrimaryRestriction = (element) => {
    const r = element.restrictions
    if (!r) return null

    if (r.startTime && r.endTime) {
      return {
        type: 'time',
        label: `${moment(r.startTime).format('hh:mm A')} - ${moment(
          r.endTime,
        ).format('hh:mm A')}`,
      }
    }

    if (r.startDate && r.endDate) {
      return {
        type: 'date',
        label: `${moment(r.startDate).format('MMM DD, YYYY')} - ${moment(
          r.endDate,
        ).format('MMM DD, YYYY')}`,
      }
    }

    if (r.minKwh !== undefined || r.maxKwh !== undefined) {
      if (r.minKwh !== undefined && r.maxKwh !== undefined) {
        return {
          type: 'energy',
          label: `${r.minKwh} - ${r.maxKwh} ${t(
            'TariffPopOverContainer.EnergyUnit',
          )}`,
        }
      }
      if (r.minKwh !== undefined)
        return {
          type: 'energy',
          label: `${t('TariffPopOverContainer.Min')}: ${r.minKwh} ${t(
            'TariffPopOverContainer.EnergyUnit',
          )}`,
        }
      if (r.maxKwh !== undefined)
        return {
          type: 'energy',
          label: `${t('TariffPopOverContainer.Max')}: ${r.maxKwh} ${t(
            'TariffPopOverContainer.EnergyUnit',
          )}`,
        }
    }
    if (r.minCurrent !== undefined || r.maxCurrent !== undefined) {
      if (r.minCurrent !== undefined && r.maxCurrent !== undefined) {
        return {
          type: 'current',
          label: `${r.minCurrent} - ${r.maxCurrent} ${t(
            'TariffPopOverContainer.AmpereUnit',
          )}`,
        }
      }
      if (r.minCurrent !== undefined)
        return {
          type: 'current',
          label: `${t('TariffPopOverContainer.Min')}: ${r.minCurrent} ${t(
            'TariffPopOverContainer.AmpereUnit',
          )}`,
        }
      if (r.maxCurrent !== undefined)
        return {
          type: 'current',
          label: `${t('TariffPopOverContainer.Max')}: ${r.maxCurrent} ${t(
            'TariffPopOverContainer.AmpereUnit',
          )}`,
        }
    }

    if (r.minPower !== undefined || r.maxPower !== undefined) {
      if (r.minPower !== undefined && r.maxPower !== undefined) {
        return {
          type: 'power',
          label: `${r.minPower} - ${r.maxPower} ${t(
            'TariffPopOverContainer.PowerUnit',
          )}`,
        }
      }
      if (r.minPower !== undefined)
        return {
          type: 'power',
          label: `${t('TariffPopOverContainer.Min')}: ${r.minPower} ${t(
            'TariffPopOverContainer.PowerUnit',
          )}`,
        }
      if (r.maxPower !== undefined)
        return {
          type: 'power',
          label: `${t('TariffPopOverContainer.Max')}: ${r.maxPower} ${t(
            'TariffPopOverContainer.PowerUnit',
          )}`,
        }
    }
    if (r.minDuration !== undefined || r.maxDuration !== undefined) {
      if (r.minDuration !== undefined && r.maxDuration !== undefined) {
        return {
          type: 'duration',
          label: `${r.minDuration} - ${r.maxDuration} ${t(
            'TariffPopOverContainer.Min',
          )}`,
        }
      }
      if (r.minDuration !== undefined)
        return {
          type: 'duration',
          label: `${t('TariffPopOverContainer.Min')}: ${r.minDuration} min`,
        }
      if (r.maxDuration !== undefined)
        return {
          type: 'duration',
          label: `${t('TariffPopOverContainer.Max')}: ${r.maxDuration} ${t(
            'TariffPopOverContainer.Min',
          )}`,
        }
    }

    if (r.dayOfWeek && r.dayOfWeek.length > 0) {
      return { type: 'dayOfWeek', label: r.dayOfWeek.join(', ') }
    }
    if (r.reservation) {
      return {
        type: 'reservation',
        label: `${t('TariffPopOverContainer.Reservation')}: ${r.reservation}`,
      }
    }

    return null
  }
  return (
    <div className={Styles.Wrapper}>
      <div className={Styles.TitleDiv}>
        <div className={Styles.TitlePopOverDiv}>
          <IFText style={{ color: Colors.text }} className={Styles.TitleText}>
            <StyledHyperLink
              hoverColor={Colors.primary}
              textColor={Colors.text}
              target="_blank"
              style={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
              href={`/tariffs/${tariff?._id}`}
            >
              {tariff?.name}
              <OpenInNewIcon
                style={{
                  fontSize: 14,
                }}
              />
            </StyledHyperLink>
          </IFText>
        </div>

        <IFText style={{ color: Colors.primary }} className={Styles.TitleText}>
          {tariff?.type}
        </IFText>
      </div>
      {series.length > 0 && (
        <div className={Styles.ChartDiv}>
          <ChartComponent
            options={options}
            series={series}
            type="rangeBar"
            height={50}
            width={'100%'}
          />
        </div>
      )}
      <div className={Styles.priceComponents}>
        {elements?.map((element, elemIdx) => {
          const primaryRestriction = getPrimaryRestriction(element)
          const isActive = isRestrictionActive(element)

          return (
            <div key={elemIdx}>
              {element.priceComponents?.map((pc, pcIdx) => (
                <div key={pcIdx}>
                  <div className={Styles.HeaderRow}>
                    <div className={Styles.PCDiv}>
                      <IFText
                        className={Styles.DetailsTextWithDiv}
                        style={{ fontWeight: isActive ? 'bold' : '' }}
                      >
                        {pc.type} {t('TariffList.Dash')} {pc.price}
                        {operator?.country.currency.code}
                      </IFText>
                      {hasTimeRestrictions(element) && (
                        <div
                          className={Styles.Indicator}
                          style={{
                            backgroundColor: isActive
                              ? getColorForElement(elemIdx)
                              : Colors.grey,
                          }}
                        />
                      )}
                    </div>
                    {primaryRestriction && (
                      <IFText
                        className={Styles.DetailsText}
                        style={{ fontWeight: isActive ? 'bold' : '' }}
                      >
                        {primaryRestriction.label}
                      </IFText>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        })}
      </div>
      <div className={Styles.Footer}>
        <div
          className={Styles.VerticalDivider}
          style={{ backgroundColor: Colors.grey }}
        />
        <div className={Styles.InfoDiv}>
          <InfoIcon
            style={{ color: Colors.tooltipGreyBackground }}
            className={Styles.InfoIcon}
          />
          <IFText
            className={Styles.InfoText}
            style={{ color: Colors.tooltipGreyBackground }}
          >
            {t('TariffPopOverContainer.InfoText')}
          </IFText>
        </div>
      </div>
    </div>
  )
}
TariffPopOverContainer.propTypes = {
  tariff: PropTypes.object,
  elements: PropTypes.array,
  operator: PropTypes.object,
}

export default TariffPopOverContainer
